/**
 * 公共工具方法
 */

/**
 * 图片基础域名（从环境变量读取）
 * 后端返回的图片路径通常是相对路径如 /upload/xxx.png
 * 开发环境通过 devServer proxy 代理 /upload 请求，无需拼接域名
 * 生产环境需要拼接完整域名
 */
export const IMG_BASE_URL = (process.env.VUE_APP_IMG_BASE_URL || '').replace(/\r/g, '')

/**
 * 拼接图片完整地址
 * @param {String} path - 后端返回的相对路径，如 /upload/b4/xxx.png
 * @returns {String} 完整图片地址
 */
export function getImageUrl(path) {
  if (!path) return ''
  // 已经是完整URL（http/https开头）则直接返回
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  // 以 /upload 开头的相对路径：开发环境走代理，生产环境拼接域名
  if (IMG_BASE_URL && process.env.NODE_ENV === 'production') {
    return IMG_BASE_URL + path
  }
  return path
}

/**
 * 格式化价格，保留两位小数
 * @param {Number} price - 价格数值
 * @returns {String} 格式化后的价格字符串
 */
export function formatPrice(price) {
  if (!price && price !== 0) return '0.00'
  return Number(price).toFixed(2)
}

/**
 * 格式化日期
 * @param {String|Date} date - 日期
 * @param {String} format - 格式 默认 YYYY-MM-DD HH:mm:ss
 * @returns {String} 格式化后的日期字符串
 */
export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!date) return ''
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

/**
 * 手机号脱敏处理
 * @param {String} phone - 手机号
 * @returns {String} 脱敏后的手机号
 */
export function maskPhone(phone) {
  if (!phone || phone.length < 11) return phone
  return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
}

/**
 * 获取订单状态文本
 * @param {Number} status - 状态码
 * @returns {String} 状态文本
 */
export function getOrderStatusText(status, payTime) {
  if (status === undefined || status === null) return '未知状态'
  console.log('getOrderStatusText', status, payTime)
  const statusMap = {
    0: '待支付',
    1: '订阅中',
    2: '已完成',
    3: '已取消',
    4: '退款/售后'
  }
  if (status === 0 && payTime === '') return '待支付'
  if (status === 1 && payTime === '') return '待确认'
  if (status === 0 && payTime !== '') return '待确认'
  if (status === 1 && payTime !== '') return '订阅中'
  return statusMap[status] || '未知状态'
}

// 服务器时间与本地时间的差值（毫秒），正值表示服务器比本地快
let _serverTimeDiff = null

/**
 * 通过接口响应头同步服务器时间
 * 在 axios 响应拦截器中调用一次即可
 * @param {string} dateStr - 响应头中的 Date 字段
 */
export function syncServerTime(dateStr) {
  if (!dateStr || _serverTimeDiff !== null) return
  try {
    const serverTime = new Date(dateStr).getTime()
    if (!isNaN(serverTime)) {
      _serverTimeDiff = serverTime - Date.now()
    }
  } catch (e) {
    // 解析失败则忽略，使用本地时间
  }
}

/**
 * 获取修正后的当前时间
 * 如果已同步服务器时间，则返回服务器时间；否则返回本地时间
 * @returns {Date}
 */
export function getNow() {
  return new Date(Date.now() + (_serverTimeDiff || 0))
}

/**
 * 根据当前日期计算可选订阅年份列表
 * 9月1日之前：[今年, 去年, 前年]
 * 9月1日之后：[明年, 今年, 去年]
 * 使用服务器时间修正，避免用户本地时间不准
 * @returns {Array<{label: string, value: number}>} 年份选项列表
 */
export function getSubscriptionYears() {
  const now = getNow()
  const currentYear = now.getFullYear()
  const month = now.getMonth() + 1 // 0-based
  const day = now.getDate()

  // 判断是否在9月1日之前
  const beforeSept1 = month < 9 || (month === 9 && day < 1)

  if (beforeSept1) {
    return [
      { label: `${currentYear}年`, value: currentYear },
      { label: `${currentYear - 1}年`, value: currentYear - 1 },
      { label: `${currentYear - 2}年`, value: currentYear - 2 }
    ]
  } else {
    return [
      { label: `${currentYear + 1}年`, value: currentYear + 1 },
      { label: `${currentYear}年`, value: currentYear },
      { label: `${currentYear - 1}年`, value: currentYear - 1 }
    ]
  }
}

/**
 * 根据选中年份和折扣配置计算折扣率
 * 9月1日之前：今年100%、去年80%、前年70%
 * 9月1日之后：明年100%、今年80%、去年70%
 * 使用服务器时间修正，与 getSubscriptionYears 保持一致
 *
 * @param {Object} zhekouConfig - 折扣配置 {before:{jin,qu,qian}, after:{jin,qu,ming}}
 * @param {Number|String} selectedYear - 选中的年份，如 2026
 * @returns {Number} 折扣率（百分比数值：100/80/70），默认 100
 */
export function getZhekouRate(zhekouConfig, selectedYear) {
  if (!zhekouConfig || selectedYear == null || selectedYear === '') return 100
  const year = parseInt(selectedYear)
  if (isNaN(year)) return 100

  const now = getNow()
  const currentYear = now.getFullYear()
  const month = now.getMonth() + 1
  const day = now.getDate()
  const beforeSept1 = month < 9 || (month === 9 && day < 1)

  let key = ''
  if (beforeSept1) {
    if (year === currentYear) key = 'jin'
    else if (year === currentYear - 1) key = 'qu'
    else if (year === currentYear - 2) key = 'qian'
  } else {
    if (year === currentYear + 1) key = 'ming'
    else if (year === currentYear) key = 'jin'
    else if (year === currentYear - 1) key = 'qu'
  }

  const config = beforeSept1 ? zhekouConfig.before : zhekouConfig.after
  return config && config[key] ? parseInt(config[key]) : 100
}
